import { useTranslation } from 'react-i18next'

import { StatusPill } from '@/components/px/StatusPill'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import type { ApiClientRow } from '@/features/clients/types'

const columnHeadClass =
  'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

/** The primary contact: first with an e-mail, else the first, else none. */
const primaryEmail = (row: ApiClientRow): string | null => {
  const { contacts = [] } = row
  const withEmail = contacts.find((contact) => (contact.email ?? '').trim() !== '')
  return withEmail?.email ?? contacts[0]?.email ?? null
}

/**
 * The client register table (PX-W6.2, AC-5): name, status pill, primary
 * contact, data room root — with per-row Edit and Archive/Restore actions and
 * an expandable detail panel slot.
 */
export function ClientsTable({
  rows,
  expandedId,
  onExpandClicked,
  onEditClicked,
  onArchiveClicked
}: {
  rows: readonly ApiClientRow[]
  expandedId: string | null
  onExpandClicked: (id: string) => void
  onEditClicked: (row: ApiClientRow) => void
  onArchiveClicked: (row: ApiClientRow) => void
}) {
  const { t } = useTranslation('clients')
  return (
    <Table>
      <TableCaption className="sr-only">{t('title')}</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead className={columnHeadClass}>{t('columns.client')}</TableHead>
          <TableHead className={columnHeadClass}>{t('columns.status')}</TableHead>
          <TableHead className={columnHeadClass}>{t('columns.contact')}</TableHead>
          <TableHead className={columnHeadClass}>{t('columns.dataRoom')}</TableHead>
          <TableHead className={columnHeadClass}> </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const archived = row.status === 'archived'
          return (
            <TableRow key={row.id} data-testid={`client-row-${row.id}`}>
              <TableCell className="py-2.5">
                <button
                  type="button"
                  onClick={() => onExpandClicked(row.id)}
                  aria-expanded={expandedId === row.id}
                  className="font-medium text-foreground underline-offset-2 hover:underline"
                >
                  {row.name}
                </button>
              </TableCell>
              <TableCell>
                <StatusPill tone={archived ? 'neutral' : 'done'}>
                  {archived ? t('status.archived') : t('status.active')}
                </StatusPill>
              </TableCell>
              <TableCell className="text-sm text-ink-soft">{primaryEmail(row) ?? '—'}</TableCell>
              <TableCell className="max-w-[16rem] truncate font-mono text-xs text-ink-soft">
                {row.dataRoomRoot ?? '—'}
              </TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => onEditClicked(row)}
                    className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted"
                  >
                    {t('common:actionsMore.edit', { ns: 'common' })}
                  </button>
                  <button
                    type="button"
                    onClick={() => onArchiveClicked(row)}
                    className={
                      archived
                        ? 'rounded-md border border-input px-2 py-1 text-xs text-done hover:bg-muted'
                        : 'rounded-md border border-input px-2 py-1 text-xs text-blocked hover:bg-muted'
                    }
                  >
                    {archived ? t('archive.restore') : t('archive.action')}
                  </button>
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}
